import _ from 'lodash'

export interface ImportIssue {
  error: string
  recommended_fix: string
}

export interface ImportHeaderPresence {
  firstname: boolean
  lastname: boolean
  email: boolean
  team_name: boolean
  boss: boolean
  language: boolean
  country: boolean
  timezone: boolean
  user_type: boolean
  nickname: boolean
  birthday: boolean
  join_date: boolean
}

export interface ImportHeaderIndex {
  firstname: number
  lastname: number
  email: number
  team_name: number
  boss: number
  language: number
  country: number
  timezone: number
  user_type: number
  nickname: number
  birthday: number
  join_date: number
}

export interface ImportOptionalFields {
  nickname: boolean
  birthday: boolean
  workdate: boolean
}

export type ImportTeamAction = 'reuse' | 'move' | 'create' | 'map'

export interface ImportTeamMappingSuggestion {
  imported_team_name: string
  imported_team_signature: string
  target_team_id: string
  target_team_name: string
  target_team_signature: string
  rename_target_team_name?: string
  confidence: number
  reason: 'near_match'
}

export interface ImportTeamMappingDecision {
  action: 'map'
  imported_team_name: string
  imported_team_signature: string
  target_team_id: string
  target_team_name: string
  target_team_signature: string
  rename_target_team_name?: string
  source: 'manual' | 'saved_alias'
  alias_key?: string
}

export interface ImportTeamAlias {
  imported_team_name: string
  imported_team_signature: string
  target_team_id: string
  target_team_name: string
  target_team_signature: string
  created_at?: string
  updated_at?: string
  created_by?: string
}

export interface ExistingImportMember {
  first_name: string
  last_name: string
  email: string
  team_name: string
  team_id: string
  team_signature: string
  direct_manager_email: string
  manager_name: string
  stored_user_type: string
  user_type: string
  language: string
  country: string
  time_zone: string
  nickname: string
  birthday: string
  workdate: string
}

export interface NormalizedImportMember {
  row: number
  first_name: string
  last_name: string
  email: string
  team_name: string
  direct_manager_email: string
  manager_name: string
  user_type: string
  language: string
  country: string
  time_zone: string
  nickname: string
  birthday: string
  workdate: string
  _issues: ImportIssue[]
  _warnings: string[]
  _profile_only: boolean
  _existing: ExistingImportMember | null
  _column_presence: ImportHeaderPresence
  _optional_fields: ImportOptionalFields
  _team_action?: ImportTeamAction
  _resolved_team_id?: string
  _target_team_signature?: string
  _source_team_signature?: string
  _suggested_team_name?: string
  _imported_team_name?: string
  _team_mapping_suggestion?: ImportTeamMappingSuggestion | null
  _team_mapping_decision?: ImportTeamMappingDecision | null
  error?: string
  note?: string
}

export interface ImportDataGroup {
  name: string
  code: string
  items: NormalizedImportMember[]
}

export interface CountryOption {
  lable: string
  value: string
}

export interface ImportDefaults {
  companyCountry: string
  defaultLanguage: string
  defaultTimezone: string
  locationOptions?: CountryOption[]
}

export interface ImportPreviewResult {
  dataGroups: ImportDataGroup[]
  total: number
  importedMembers: NormalizedImportMember[]
  existingByEmail: Map<string, ExistingImportMember>
}

export interface ImportPlanIdFactory {
  nextTeamId: () => string
  nextGenBossId: () => string
  nextGenOrganizeId: () => string
}

export interface ImportMutationLog {
  id: string
  table: 'users' | 'team'
  status: 'add' | 'edit'
  source?: string
}

export interface ImportMutationPlan {
  updates: Record<string, any>
  createdUsers: string[]
  updatedUsers: string[]
  createdTeams: string[]
  touchedTeamIds: string[]
  savedTeamAliases: string[]
  logs: ImportMutationLog[]
}

const ROOT_GROUPS = (): ImportDataGroup[] => [{
  name: 'New Members to Add',
  code: 'new',
  items: [],
}, {
  name: 'Members to Update',
  code: 'update',
  items: [],
}, {
  name: 'Members to Archive',
  code: 'archive',
  items: [],
}, {
  name: 'Issues to Resolve',
  code: 'resolve',
  items: [],
}]

export const IMPORT_LANGUAGE_ALIASES: Record<string, string> = {
  en: 'en',
  eng: 'en',
  english: 'en',
  'อังกฤษ': 'en',
  th: 'th',
  thai: 'th',
  'ไทย': 'th',
  my: 'my',
  burmese: 'my',
  myanmar: 'my',
  'မြန်မာ': 'my',
  ms: 'ms',
  malay: 'ms',
  'bahasa malaysia': 'ms',
  'zh hk': 'zh-HK',
  'zh-hk': 'zh-HK',
  'traditional chinese': 'zh-HK',
  'chinese traditional hong kong': 'zh-HK',
  'chinese traditional': 'zh-HK',
  'pt br': 'pt-br',
  'pt-br': 'pt-br',
  'brazilian portuguese': 'pt-br',
}

export const IMPORT_USER_TYPE_ALIASES: Record<string, string> = {
  user: 'user',
  member: 'user',
  employee: 'user',
  admin: 'admin',
  administrator: 'admin',
  developer: 'developer',
  dev: 'developer',
  hr: 'hr',
  'human resources': 'hr',
  leader: 'leadership',
  leadership: 'leadership',
  manager: 'leadership',
  superleader: 'superleader',
  'super leader': 'superleader',
  superadmin: 'superadmin',
  'super admin': 'superadmin',
  ghost: 'ghost',
}

export const IMPORT_USER_TYPE_OPTIONS = [{
  value: 'user',
  label: 'Member',
}, {
  value: 'admin',
  label: 'Admin',
}, {
  value: 'developer',
  label: 'Developer',
}, {
  value: 'hr',
  label: 'HR',
}, {
  value: 'leadership',
  label: 'Leader',
}, {
  value: 'superleader',
  label: 'Super Leader',
}, {
  value: 'superadmin',
  label: 'Super Admin',
}]

export const normalizeImportString = (value: any) => {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

export const normalizeImportEmail = (value: any) => {
  const normalized = normalizeImportString(value)
  if (!normalized) return ''

  if (normalized.indexOf('@') !== -1) {
    return normalized.toLowerCase().replace(/\s+/g, '')
  }

  const isPhoneLike = /^(?:\+|00)?[\d\s().-]+$/.test(normalized)
  if (!isPhoneLike) {
    return normalized.toLowerCase().replace(/\s+/g, '')
  }

  let phone = normalized.replace(/[\s().-]+/g, '')
  if (phone.startsWith('00')) {
    phone = `+${phone.slice(2)}`
  }
  if (phone.startsWith('+')) {
    return `+${phone.slice(1).replace(/\D+/g, '')}`
  }
  return phone.replace(/\D+/g, '')
}

export const normalizeImportHeader = (value: any) => {
  return normalizeImportString(value)
    .toLowerCase()
    .replace(/[()/]/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const TEAM_MAPPING_SCORE_THRESHOLD = 0.86
const CROSS_PARENT_TEAM_MAPPING_SCORE_THRESHOLD = 0.92

const TEAM_NAME_TOKEN_ALIASES: Record<string, string[]> = {
  admin: ['administration'],
  bizdev: ['business', 'development'],
  cs: ['customer', 'success'],
  cx: ['customer', 'experience'],
  dev: ['development'],
  eng: ['engineering'],
  fin: ['finance'],
  hr: ['human', 'resources'],
  it: ['information', 'technology'],
  mktg: ['marketing'],
  ops: ['operations'],
  po: ['people', 'operations'],
  prod: ['product'],
  qa: ['quality', 'assurance'],
}

const TEAM_NAME_STOP_WORDS = new Set([
  'and',
  'business',
  'department',
  'dept',
  'division',
  'group',
  'management',
  'support',
  'team',
  'unit',
])

const normalizeTeamNameForSimilarity = (value: any) => {
  return normalizeImportString(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const buildTeamNameTokens = (value: any) => {
  const rawTokens = normalizeTeamNameForSimilarity(value)
    .split(' ')
    .filter(Boolean)
    .filter((token) => !TEAM_NAME_STOP_WORDS.has(token))
  const expandedTokens: string[] = []
  rawTokens.forEach((token) => {
    if (TEAM_NAME_TOKEN_ALIASES[token]) {
      expandedTokens.push(...TEAM_NAME_TOKEN_ALIASES[token])
    } else {
      expandedTokens.push(token)
    }
  })
  return _.uniq(expandedTokens)
}

const calculateLevenshteinDistance = (left: string, right: string) => {
  const rows = left.length + 1
  const cols = right.length + 1
  const distance: number[][] = []

  for (let row = 0; row < rows; row++) {
    distance[row] = [row]
  }
  for (let col = 1; col < cols; col++) {
    distance[0][col] = col
  }

  for (let row = 1; row < rows; row++) {
    for (let col = 1; col < cols; col++) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1
      distance[row][col] = Math.min(
        distance[row - 1][col] + 1,
        distance[row][col - 1] + 1,
        distance[row - 1][col - 1] + cost
      )
    }
  }

  return distance[left.length][right.length]
}

const scoreTeamNameSimilarity = (left: string, right: string) => {
  const leftTokens = buildTeamNameTokens(left)
  const rightTokens = buildTeamNameTokens(right)
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }

  const leftCanonical = leftTokens.join(' ')
  const rightCanonical = rightTokens.join(' ')
  if (leftCanonical === rightCanonical) {
    return 1
  }

  const leftCompact = leftTokens.join('')
  const rightCompact = rightTokens.join('')
  if (leftCompact === rightCompact) {
    return 1
  }

  const leftAcronym = leftTokens.map((token) => token.charAt(0)).join('')
  const rightAcronym = rightTokens.map((token) => token.charAt(0)).join('')
  if ((leftCompact.length >= 2 && leftCompact === rightAcronym) || (rightCompact.length >= 2 && rightCompact === leftAcronym)) {
    return 0.92
  }

  const leftSet = new Set(leftTokens)
  const rightSet = new Set(rightTokens)
  let intersection = 0
  leftSet.forEach((token) => {
    if (rightSet.has(token)) {
      intersection++
    }
  })
  const union = _.uniq(leftTokens.concat(rightTokens)).length
  const tokenScore = union > 0 ? intersection / union : 0

  const maxLength = Math.max(leftCompact.length, rightCompact.length)
  const editScore = maxLength > 0
    ? 1 - (calculateLevenshteinDistance(leftCompact, rightCompact) / maxLength)
    : 0

  return Math.max(tokenScore, editScore)
}

const getTeamSignatureParent = (signature: string) => {
  if (!signature || signature.indexOf('>') === -1) return ''
  return signature.split('>').slice(0, -1).join('>')
}

const buildRenamedTeamSignature = (signature: string, teamName: string) => {
  const normalizedName = normalizeImportHeader(teamName)
  if (!normalizedName) return signature
  const parentSignature = getTeamSignatureParent(signature)
  return parentSignature ? `${parentSignature}>${normalizedName}` : normalizedName
}

export const buildTeamImportAliasKey = (importedTeamName: string, importedTeamSignature: string) => {
  const seed = `${normalizeImportHeader(importedTeamName)}|${normalizeImportHeader(importedTeamSignature)}`
  let hash = 5381
  for (let index = 0; index < seed.length; index++) {
    hash = ((hash << 5) + hash) + seed.charCodeAt(index)
    hash = hash & hash
  }
  return `team_alias_${Math.abs(hash).toString(36)}`
}

type ImportHeaderAlias = string | {
  value: string
  exact?: boolean
}

const exactImportHeaderAlias = (value: string): ImportHeaderAlias => ({ value, exact: true })

const normalizeImportHeaderAlias = (alias: ImportHeaderAlias) => {
  if (typeof alias === 'string') {
    return {
      value: normalizeImportHeader(alias),
      exact: false,
    }
  }

  return {
    value: normalizeImportHeader(alias.value),
    exact: !!alias.exact,
  }
}

const matchesImportHeaderAlias = (cell: string, alias: { value: string, exact: boolean }) => {
  if (!alias.value) return false
  if (alias.exact) return cell === alias.value

  return cell === alias.value
    || cell.startsWith(`${alias.value} `)
    || cell.includes(` ${alias.value} `)
}

export const findImportHeaderIndex = (header: any[], aliases: ImportHeaderAlias[], fallback = -1) => {
  const normalizedHeader = header.map(normalizeImportHeader)
  const aliasList = aliases.map(normalizeImportHeaderAlias)
  // Alias order is a priority list: an earlier alias in any column must win over
  // a later alias in an earlier column (e.g. 'Email Address' beats 'Phone Number').
  for (let aliasIndex = 0; aliasIndex < aliasList.length; aliasIndex++) {
    const alias = aliasList[aliasIndex]
    const index = normalizedHeader.findIndex((cell: string) => matchesImportHeaderAlias(cell, alias))
    if (index !== -1) {
      return index
    }
  }
  return fallback
}

export const buildImportHeaderIndex = (header: any[]): ImportHeaderIndex => {
  return {
    firstname: findImportHeaderIndex(header, [
      'first name',
      exactImportHeaderAlias('firstname'),
      'given name',
      exactImportHeaderAlias('given'),
      exactImportHeaderAlias('first'),
    ], -1),
    lastname: findImportHeaderIndex(header, [
      'last name',
      exactImportHeaderAlias('lastname'),
      'family name',
      exactImportHeaderAlias('surname'),
      exactImportHeaderAlias('last'),
    ], -1),
    email: findImportHeaderIndex(header, [
      'email address or phone number',
      'email or phone number',
      'email address',
      'work email address',
      'business email address',
      'company email address',
      'employee email address',
      exactImportHeaderAlias('email'),
      exactImportHeaderAlias('e mail'),
      'phone number',
      'mobile number',
      'work email',
      'business email',
      'company email',
      'employee email',
      'work phone',
      'mobile phone',
      'identifier',
      'member identifier',
      'employee identifier',
      'employee id',
    ], -1),
    team_name: findImportHeaderIndex(header, [
      'team name',
      exactImportHeaderAlias('team'),
      'department name',
      exactImportHeaderAlias('department'),
      'division name',
      exactImportHeaderAlias('division'),
      'group name',
      exactImportHeaderAlias('group'),
      exactImportHeaderAlias('unit'),
      'org unit',
      'organization unit',
    ], -1),
    boss: findImportHeaderIndex(header, [
      'direct manager email or phone number',
      'direct manager email or phone',
      'direct manager email',
      'direct manager phone number',
      'direct manager phone',
      'manager email',
      'manager email address',
      'manager phone number',
      'manager phone',
      'manager identifier',
      'direct manager identifier',
      'direct manager',
      exactImportHeaderAlias('manager'),
      'reporting manager',
      'reports to',
      'reports-to',
      'line manager',
      'supervisor email',
      'supervisor phone number',
      'supervisor phone',
      'supervisor identifier',
      'direct supervisor',
      exactImportHeaderAlias('supervisor'),
    ], -1),
    language: findImportHeaderIndex(header, [
      'language',
      'preferred language',
      exactImportHeaderAlias('locale'),
    ], -1),
    country: findImportHeaderIndex(header, [
      'country',
      'work country',
      'office country',
      exactImportHeaderAlias('location'),
    ], -1),
    timezone: findImportHeaderIndex(header, [
      'time-zone',
      'timezone',
      'time zone',
      'utc offset',
      'gmt offset',
    ], -1),
    user_type: findImportHeaderIndex(header, [
      'user type',
      'user role',
      'member type',
      'access level',
      'role permissions',
      'permissions',
      'permission',
      // Intentionally no bare 'role' alias: HR exports commonly use 'Role' for
      // job titles, and titles like 'Manager' would silently map to elevated
      // app permissions through IMPORT_USER_TYPE_ALIASES.
    ], -1),
    nickname: findImportHeaderIndex(header, [
      'nickname',
      'preferred name',
      'display name',
    ], -1),
    birthday: findImportHeaderIndex(header, [
      'birthday',
      'birth date',
      'date of birth',
      exactImportHeaderAlias('dob'),
    ], -1),
    join_date: findImportHeaderIndex(header, [
      'join date',
      'start date',
      'work date',
      'hire date',
      'date joined',
      'joined date',
      'employment start date',
    ], -1),
  }
}

export const buildImportHeaderPresence = (headerIndex: ImportHeaderIndex): ImportHeaderPresence => {
  return {
    firstname: headerIndex.firstname !== -1,
    lastname: headerIndex.lastname !== -1,
    email: headerIndex.email !== -1,
    team_name: headerIndex.team_name !== -1,
    boss: headerIndex.boss !== -1,
    language: headerIndex.language !== -1,
    country: headerIndex.country !== -1,
    timezone: headerIndex.timezone !== -1,
    user_type: headerIndex.user_type !== -1,
    nickname: headerIndex.nickname !== -1,
    birthday: headerIndex.birthday !== -1,
    join_date: headerIndex.join_date !== -1,
  }
}

export const isValidImportEmail = (email: string) => {
  // '#', '$', '[', ']' and '/' are illegal in Firebase RTDB keys; emails are
  // used as user keys (with '.' -> '||'), so reject them up front.
  if (/[#$\[\]/]/.test(email)) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export const isValidImportPhone = (phone: string) => {
  return /^\+?[1-9]\d{6,14}$/.test(phone)
}

export const isValidImportIdentifier = (value: string) => {
  return isValidImportEmail(value) || isValidImportPhone(value)
}

export const buildImportName = (firstName: string, lastName: string) => {
  return [normalizeImportString(firstName), normalizeImportString(lastName)]
    .filter(Boolean)
    .join(' ')
}

const buildInitialsFromParts = (parts: string[]) => {
  return parts
    .map((part) => normalizeImportString(part))
    .filter(Boolean)
    .map((part) => part.replace(/[^A-Za-z0-9]+/g, ''))
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 4)
}

const buildManagerInitials = (manager: any) => {
  const nameInitials = buildInitialsFromParts([
    _.get(manager, 'first_name', ''),
    _.get(manager, 'last_name', ''),
  ])
  if (nameInitials) {
    return nameInitials
  }

  const email = normalizeImportEmail(_.get(manager, 'email', ''))
  if (email.indexOf('@') !== -1) {
    const localPart = email.split('@')[0].replace(/[^A-Za-z0-9]+/g, ' ')
    const emailInitials = buildInitialsFromParts(localPart.split(/\s+/))
    if (emailInitials) {
      return emailInitials
    }
  }

  return normalizeImportString(email).replace(/[^A-Za-z0-9]+/g, '').slice(0, 2).toUpperCase()
}

const buildManagerLabel = (manager: any) => {
  const name = buildImportName(_.get(manager, 'first_name', ''), _.get(manager, 'last_name', ''))
  if (name) {
    return name
  }

  const email = normalizeImportEmail(_.get(manager, 'email', ''))
  if (email) {
    return email
  }

  return 'their direct manager'
}

const escapeImportRegExp = (value: string) => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const sanitizeTeamNameSuffix = (value: any) => {
  return normalizeImportString(value)
    .replace(/[<>/\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const buildManagerDisambiguatingSuffix = (manager: any) => {
  const name = sanitizeTeamNameSuffix(buildImportName(_.get(manager, 'first_name', ''), _.get(manager, 'last_name', '')))
  if (name) {
    return name
  }

  const email = normalizeImportEmail(_.get(manager, 'email', ''))
  if (email.indexOf('@') !== -1) {
    const localPart = sanitizeTeamNameSuffix(email.split('@')[0].replace(/[^A-Za-z0-9]+/g, ' '))
    if (localPart) {
      return localPart
    }
  }

  return buildManagerInitials(manager)
}

const stripRepeatedManagedTeamSuffixes = (teamName: string, suffixes: string[]) => {
  let normalizedTeamName = normalizeImportString(teamName)
  const normalizedSuffixes = _.uniq(suffixes.map(sanitizeTeamNameSuffix).filter(Boolean))
  if (!normalizedTeamName || normalizedSuffixes.length === 0) {
    return normalizedTeamName
  }

  let removedSuffix = true
  while (removedSuffix && normalizedTeamName) {
    removedSuffix = false
    normalizedSuffixes.forEach((suffix) => {
      if (removedSuffix) {
        return
      }
      const suffixPattern = new RegExp(`\\s*-\\s*${escapeImportRegExp(suffix)}\\s*$`, 'i')
      if (suffixPattern.test(normalizedTeamName)) {
        normalizedTeamName = normalizedTeamName.replace(suffixPattern, '').trim()
        removedSuffix = true
      }
    })
  }

  return normalizedTeamName
}

const buildSuggestedManagedTeamName = (teamName: string, manager: any, preferredSuffix = '') => {
  if (!normalizeImportString(teamName)) {
    return ''
  }

  const initials = buildManagerInitials(manager)
  const suffix = sanitizeTeamNameSuffix(preferredSuffix) || initials
  if (!suffix) {
    return ''
  }

  const managerSuffix = buildManagerDisambiguatingSuffix(manager)
  const normalizedTeamName = stripRepeatedManagedTeamSuffixes(teamName, [suffix, initials, managerSuffix])
  if (!normalizedTeamName) {
    return ''
  }

  const suggestedName = `${normalizedTeamName} - ${suffix}`
  return normalizeImportHeader(normalizedTeamName) === normalizeImportHeader(suggestedName)
    ? normalizedTeamName
    : suggestedName
}

const buildRenameTeamSuggestion = (teamName: string, manager: any, suggestedNameOverride = '') => {
  const suggestedName = suggestedNameOverride || buildSuggestedManagedTeamName(teamName, manager)
  if (!suggestedName || normalizeImportString(suggestedName) === normalizeImportString(teamName)) {
    return ''
  }
  return ` For example, rename it to '${suggestedName}'.`
}

const buildTeamReportingLineFix = (teamName: string, manager: any, suggestedName = '') => {
  return `Use one team name for one org location only, or rename the team for ${buildManagerLabel(manager)}.${buildRenameTeamSuggestion(teamName, manager, suggestedName)}`
}

const buildReportingLineSuggestedTeamNames = (
  teamMembers: NormalizedImportMember[],
  finalMembersByEmail: Map<string, any>
) => {
  const suggestionsBySignature = new Map<string, string>()
  const entriesBySignature = new Map<string, {
    member: NormalizedImportMember
    manager: any
    defaultSuggestedName: string
  }>()

  teamMembers.forEach((member) => {
    const signature = member._target_team_signature || ''
    if (entriesBySignature.has(signature)) {
      return
    }
    const manager = finalMembersByEmail.get(member.direct_manager_email) as any
    entriesBySignature.set(signature, {
      member,
      manager,
      defaultSuggestedName: buildSuggestedManagedTeamName(member.team_name, manager),
    })
  })

  const defaultSuggestionCounts = new Map<string, number>()
  entriesBySignature.forEach((entry) => {
    const normalizedSuggestion = normalizeImportHeader(entry.defaultSuggestedName)
    if (!normalizedSuggestion) {
      return
    }
    defaultSuggestionCounts.set(normalizedSuggestion, (defaultSuggestionCounts.get(normalizedSuggestion) || 0) + 1)
  })

  entriesBySignature.forEach((entry, signature) => {
    const normalizedOriginalName = normalizeImportHeader(entry.member.team_name)
    const normalizedDefaultSuggestion = normalizeImportHeader(entry.defaultSuggestedName)
    const duplicateDefaultSuggestion = !!normalizedDefaultSuggestion &&
      (defaultSuggestionCounts.get(normalizedDefaultSuggestion) || 0) > 1

    let suggestedName = entry.defaultSuggestedName
    if (!suggestedName || duplicateDefaultSuggestion || normalizedDefaultSuggestion === normalizedOriginalName) {
      const disambiguatingSuffix = buildManagerDisambiguatingSuffix(entry.manager)
      const disambiguatingSuggestedName = buildSuggestedManagedTeamName(
        entry.member.team_name,
        entry.manager,
        disambiguatingSuffix
      )
      if (disambiguatingSuggestedName && normalizeImportHeader(disambiguatingSuggestedName) !== normalizedOriginalName) {
        suggestedName = disambiguatingSuggestedName
      }
    }

    if (suggestedName) {
      suggestionsBySignature.set(signature, suggestedName)
    }
  })

  return suggestionsBySignature
}

const buildAmbiguousTeamMatchFix = (teamName: string, manager: any) => {
  return `Rename the team or import the full affected team so the team can be matched safely.${buildRenameTeamSuggestion(teamName, manager)}`
}

const buildPartialTeamMoveFix = (teamName: string, manager: any) => {
  return `Import the full team before moving it to ${buildManagerLabel(manager)}, or rename the team if this should stay a separate manager-owned team.${buildRenameTeamSuggestion(teamName, manager)}`
}

export const normalizeImportLanguage = (value: any, fallback: string) => {
  const normalized = normalizeImportHeader(value)
  if (!normalized) return fallback
  if (IMPORT_LANGUAGE_ALIASES[normalized]) return IMPORT_LANGUAGE_ALIASES[normalized]
  return fallback
}

export const normalizeImportUserType = (value: any, fallback: string) => {
  const normalized = normalizeImportHeader(value)
  if (!normalized) return fallback
  if (IMPORT_USER_TYPE_ALIASES[normalized]) return IMPORT_USER_TYPE_ALIASES[normalized]
  return fallback
}

const buildCountryCodeMaps = (locationOptions: CountryOption[] = []) => {
  const codeMap = new Map<string, string>()
  const nameMap = new Map<string, string>()
  locationOptions.forEach((item) => {
    const code = normalizeImportString(item.lable).toUpperCase()
    const name = normalizeImportHeader(item.value)
    if (code) {
      codeMap.set(code, code)
      codeMap.set(normalizeImportHeader(code), code)
    }
    if (name) {
      nameMap.set(name, code)
    }
  })
  return { codeMap, nameMap }
}

export const normalizeImportCountry = (value: any, fallback: string, locationOptions: CountryOption[] = []) => {
  const normalized = normalizeImportString(value)
  if (!normalized) return fallback
  const upper = normalized.toUpperCase()
  const { codeMap, nameMap } = buildCountryCodeMaps(locationOptions)
  if (codeMap.has(upper)) return codeMap.get(upper) as string
  const normalizedHeaderValue = normalizeImportHeader(normalized)
  if (codeMap.has(normalizedHeaderValue)) return codeMap.get(normalizedHeaderValue) as string
  if (nameMap.has(normalizedHeaderValue)) return nameMap.get(normalizedHeaderValue) as string
  if (normalized.toLowerCase() === 'thailand') return 'TH'
  return fallback
}

const withFallbackWarning = (rawValue: any, fallbackValue: string, fieldLabel: string) => {
  const raw = normalizeImportString(rawValue)
  if (!raw) {
    return {
      value: fallbackValue,
      warning: '',
    }
  }
  return {
    value: fallbackValue,
    warning: `${fieldLabel} '${raw}' is not recognized. Using ${fallbackValue}.`,
  }
}

export const normalizeImportLanguageWithWarning = (value: any, fallback: string) => {
  const normalized = normalizeImportHeader(value)
  if (!normalized) {
    return {
      value: fallback,
      warning: '',
    }
  }
  if (IMPORT_LANGUAGE_ALIASES[normalized]) {
    return {
      value: IMPORT_LANGUAGE_ALIASES[normalized],
      warning: '',
    }
  }
  return withFallbackWarning(value, fallback, 'Language')
}

export const normalizeImportUserTypeWithWarning = (value: any, fallback: string) => {
  const normalized = normalizeImportHeader(value)
  if (!normalized) {
    return {
      value: fallback,
      warning: '',
    }
  }
  if (IMPORT_USER_TYPE_ALIASES[normalized]) {
    return {
      value: IMPORT_USER_TYPE_ALIASES[normalized],
      warning: '',
    }
  }
  return withFallbackWarning(value, fallback, 'User Type')
}

export const normalizeImportCountryWithWarning = (value: any, fallback: string, locationOptions: CountryOption[] = []) => {
  const raw = normalizeImportString(value)
  if (!raw) {
    return {
      value: fallback,
      warning: '',
    }
  }
  const normalized = normalizeImportCountry(raw, '', locationOptions)
  if (normalized) {
    return {
      value: normalized,
      warning: '',
    }
  }
  return withFallbackWarning(raw, fallback, 'Country')
}

const offsetNumberToString = (value: number) => {
  if (isNaN(value) || value < -12 || value > 14) {
    return null
  }

  const sign = value >= 0 ? '+' : '-'
  const absolute = Math.abs(value)
  const hours = Math.floor(absolute)
  const fraction = Number((absolute - hours).toFixed(2))
  const minuteLookup: Record<string, number> = {
    '0': 0,
    '0.25': 15,
    '0.5': 30,
    '0.75': 45,
  }
  const minutes = minuteLookup[String(fraction)]
  if (minutes === undefined) {
    return null
  }

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function offsetStringToNumber(value: any) {
  const raw = normalizeImportString(value)
  if (!raw) return 0
  const colonMatch = raw.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1
    const hours = parseInt(colonMatch[2], 10)
    const minutes = parseInt(colonMatch[3], 10)
    return sign * (hours + minutes / 60)
  }
  const numeric = Number(raw)
  return isNaN(numeric) ? 0 : numeric
}

export const normalizeImportTimezone = (input: any, fallback: any) => {
  const fallbackNumber = typeof fallback === 'number' ? fallback : Number(fallback)
  const fallbackOffset = typeof fallback === 'string'
    ? offsetStringToNumber(fallback)
    : fallbackNumber
  const fallbackValue = offsetNumberToString(fallbackOffset)
    || offsetNumberToString(fallbackNumber)
    || '+07:00'
  const raw = normalizeImportString(input).replace(/^(?:utc|gmt)\s*/i, '')

  if (!raw) {
    return { value: fallbackValue }
  }

  const colonMatch = raw.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1
    const hours = parseInt(colonMatch[2], 10)
    const minutes = parseInt(colonMatch[3], 10)
    if ([0, 15, 30, 45].indexOf(minutes) === -1) {
      return {
        value: fallbackValue,
        error: 'Timezone must use 00, 15, 30, or 45 minute increments.',
      }
    }
    const decimal = sign * (hours + minutes / 60)
    const normalized = offsetNumberToString(decimal)
    if (normalized) {
      return { value: normalized }
    }
    return {
      value: fallbackValue,
      error: 'Timezone must be between UTC-12 and UTC+14.',
    }
  }

  const numeric = Number(raw)
  if (!isNaN(numeric)) {
    const normalized = offsetNumberToString(numeric)
    if (normalized) {
      return { value: normalized }
    }
  }

  return {
    value: fallbackValue,
    error: 'Timezone must be between UTC-12 and UTC+14.',
  }
}

const isLeapYear = (year: number) => {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0)
}

const isValidDateParts = (year: number, month: number, day: number) => {
  if (!year || month < 1 || month > 12 || day < 1) return false
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= daysInMonth[month - 1]
}

export const normalizeImportDate = (input: any) => {
  const raw = normalizeImportString(input)
  if (!raw) {
    return { value: '' }
  }

  let year = 0
  let month = 0
  let day = 0
  let warning = ''

  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    year = parseInt(parts[0], 10)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    year = parseInt(parts[2], 10)
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
      if (first <= 12 && second <= 12 && first !== second) {
        warning = `Join date '${raw}' was read as month ${month}, day ${day}. Use YYYY-MM-DD to avoid ambiguity.`
      }
    }
  } else {
    return {
      value: '',
      error: 'Join date must use YYYY-MM-DD.',
    }
  }

  if (year < 1900 || year > 2100) {
    return {
      value: '',
      error: 'Join date year must be a Gregorian year between 1900 and 2100.',
    }
  }

  if (!isValidDateParts(year, month, day)) {
    return {
      value: '',
      error: 'Join date is invalid.',
    }
  }

  return {
    value: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    warning,
  }
}

export const normalizeImportBirthday = (input: any) => {
  const raw = normalizeImportString(input)
  if (!raw) {
    return { value: '' }
  }

  let month = 0
  let day = 0
  let warning = ''

  if (/^\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    month = parseInt(parts[0], 10)
    day = parseInt(parts[1], 10)
    if (month <= 12 && day <= 12 && month !== day) {
      warning = `Birthday '${raw}' was read as month ${month}, day ${day}. Use MM-DD to avoid ambiguity.`
    }
  } else if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
      if (first <= 12 && second <= 12 && first !== second) {
        warning = `Birthday '${raw}' was read as month ${month}, day ${day}. Use MM-DD to avoid ambiguity.`
      }
    }
  } else {
    return {
      value: '',
      error: 'Birthday must use MM-DD. Full dates are accepted, but only month and day are stored.',
    }
  }

  if (!isValidDateParts(2000, month, day)) {
    return {
      value: '',
      error: 'Birthday is invalid.',
    }
  }

  return {
    value: `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    warning,
  }
}

export const normalizeStoredBirthday = (input: any) => {
  const normalized = normalizeImportBirthday(input)
  return normalized.value || normalizeImportString(input)
}

export const normalizeStoredWorkdate = (input: any) => {
  const normalized = normalizeImportDate(input)
  return normalized.value || normalizeImportString(input)
}

const createMissingIdentifierResult = (): ImportPreviewResult => {
  const item = {
    row: 1,
    first_name: '',
    last_name: '',
    email: '',
    team_name: '',
    direct_manager_email: '',
    manager_name: '',
    user_type: 'user',
    language: 'en',
    country: '',
    time_zone: '+07:00',
    nickname: '',
    birthday: '',
    workdate: '',
    _issues: [],
    _warnings: [],
    _profile_only: false,
    _existing: null,
    _column_presence: {
      firstname: false,
      lastname: false,
      email: true,
      team_name: false,
      boss: false,
      language: false,
      country: false,
      timezone: false,
      user_type: false,
      nickname: false,
      birthday: false,
      join_date: false,
    },
    _optional_fields: {
      nickname: false,
      birthday: false,
      workdate: false,
    },
    error: 'Email Address or Phone Number column is required',
    note: 'Include an Email Address or Phone Number column header so members can be matched correctly.',
  } as NormalizedImportMember
  return {
    dataGroups: [{
      name: 'Issues to Resolve',
      code: 'resolve',
      items: [item],
    }],
    total: 1,
    importedMembers: [item],
    existingByEmail: new Map(),
  }
}

const cloneHeaderPresence = (presence: ImportHeaderPresence): ImportHeaderPresence => ({
  firstname: presence.firstname,
  lastname: presence.lastname,
  email: presence.email,
  team_name: presence.team_name,
  boss: presence.boss,
  language: presence.language,
  country: presence.country,
  timezone: presence.timezone,
  user_type: presence.user_type,
  nickname: presence.nickname,
  birthday: presence.birthday,
  join_date: presence.join_date,
})

const cloneOptionalFields = (optionalFields: ImportOptionalFields): ImportOptionalFields => ({
  nickname: optionalFields.nickname,
  birthday: optionalFields.birthday,
  workdate: optionalFields.workdate,
})

const buildCurrentTeamSignature = (
  teamId: string,
  teamByKey: Record<string, any>,
  cache: Map<string, string>,
  visiting = new Set<string>()
): string => {
  if (!teamId) return ''
  if (cache.has(teamId)) {
    return cache.get(teamId) as string
  }
  if (visiting.has(teamId)) {
    return normalizeImportHeader(_.get(teamByKey, `${teamId}.name`, ''))
  }
  visiting.add(teamId)
  const team = teamByKey[teamId]
  if (!team) {
    cache.set(teamId, '')
    visiting.delete(teamId)
    return ''
  }
  const name = normalizeImportHeader(team.name)
  const parentSignature = team.parent
    ? buildCurrentTeamSignature(team.parent, teamByKey, cache, visiting)
    : ''
  const signature = parentSignature ? `${parentSignature}>${name}` : name
  cache.set(teamId, signature)
  visiting.delete(teamId)
  return signature
}

const cloneTeamMappingDecision = (value: any): ImportTeamMappingDecision | null => {
  if (!value || _.get(value, 'action') !== 'map') {
    return null
  }

  const targetTeamId = normalizeImportString(_.get(value, 'target_team_id', ''))
  const targetTeamName = normalizeImportString(_.get(value, 'target_team_name', ''))
  const targetTeamSignature = normalizeImportHeader(_.get(value, 'target_team_signature', ''))
  const renameTargetTeamName = normalizeImportString(_.get(value, 'rename_target_team_name', ''))
  if (!targetTeamId || !targetTeamName || !targetTeamSignature) {
    return null
  }

  return {
    action: 'map',
    imported_team_name: normalizeImportString(_.get(value, 'imported_team_name', '')),
    imported_team_signature: normalizeImportHeader(_.get(value, 'imported_team_signature', '')),
    target_team_id: targetTeamId,
    target_team_name: targetTeamName,
    target_team_signature: targetTeamSignature,
    rename_target_team_name: renameTargetTeamName && renameTargetTeamName !== targetTeamName ? renameTargetTeamName : undefined,
    source: _.get(value, 'source', '') === 'saved_alias' ? 'saved_alias' : 'manual',
    alias_key: normalizeImportString(_.get(value, 'alias_key', '')),
  }
}

const buildMappingDecisionFromTarget = (args: {
  importedTeamName: string
  importedTeamSignature: string
  targetTeamId: string
  teamByKey: Record<string, any>
  signatureCache: Map<string, string>
  source: 'manual' | 'saved_alias'
  aliasKey?: string
  renameTargetTeamName?: string
}): ImportTeamMappingDecision | null => {
  const targetTeam = args.teamByKey[args.targetTeamId]
  if (!targetTeam) {
    return null
  }

  const targetTeamName = normalizeImportString(targetTeam.name)
  const renameTargetTeamName = args.source === 'manual'
    ? normalizeImportString(args.renameTargetTeamName)
    : ''

  return {
    action: 'map',
    imported_team_name: normalizeImportString(args.importedTeamName),
    imported_team_signature: normalizeImportHeader(args.importedTeamSignature),
    target_team_id: args.targetTeamId,
    target_team_name: targetTeamName,
    target_team_signature: buildCurrentTeamSignature(args.targetTeamId, args.teamByKey, args.signatureCache),
    rename_target_team_name: renameTargetTeamName && renameTargetTeamName !== targetTeamName ? renameTargetTeamName : undefined,
    source: args.source,
    alias_key: args.aliasKey || '',
  }
}

const getSavedTeamAliasDecision = (
  teamImportAliases: Record<string, any>,
  importedTeamName: string,
  importedTeamSignature: string,
  teamByKey: Record<string, any>,
  signatureCache: Map<string, string>
) => {
  const aliasKey = buildTeamImportAliasKey(importedTeamName, importedTeamSignature)
  const normalizedImportedSignature = normalizeImportHeader(importedTeamSignature)
  const directAlias = _.get(teamImportAliases, aliasKey, null)
  const aliasEntry = directAlias || _.find(_.values(teamImportAliases || {}), (alias: any) => {
    return normalizeImportHeader(_.get(alias, 'imported_team_signature', '')) === normalizedImportedSignature
  })
  const resolvedAliasKey = directAlias
    ? aliasKey
    : (_.findKey(teamImportAliases || {}, (alias: any) => alias === aliasEntry) || '')

  if (!aliasEntry) {
    return null
  }

  const targetTeamId = normalizeImportString(_.get(aliasEntry, 'target_team_id', ''))
  return buildMappingDecisionFromTarget({
    importedTeamName,
    importedTeamSignature,
    targetTeamId,
    teamByKey,
    signatureCache,
    source: 'saved_alias',
    aliasKey: resolvedAliasKey || aliasKey,
  })
}

const findNearTeamMatches = (
  importedTeamName: string,
  importedTeamSignature: string,
  teamByKey: Record<string, any>,
  signatureCache: Map<string, string>
) => {
  const importedParentSignature = getTeamSignatureParent(importedTeamSignature)
  const candidates: Array<ImportTeamMappingSuggestion & { same_parent: boolean }> = []

  Object.keys(teamByKey || {}).forEach((teamId) => {
    const targetSignature = buildCurrentTeamSignature(teamId, teamByKey, signatureCache)
    if (!targetSignature) {
      return
    }

    const targetName = normalizeImportString(_.get(teamByKey, `${teamId}.name`, ''))
    const confidence = scoreTeamNameSimilarity(importedTeamName, targetName)
    if (confidence < TEAM_MAPPING_SCORE_THRESHOLD) {
      return
    }

    const sameParent = getTeamSignatureParent(targetSignature) === importedParentSignature
    candidates.push({
      imported_team_name: normalizeImportString(importedTeamName),
      imported_team_signature: normalizeImportHeader(importedTeamSignature),
      target_team_id: teamId,
      target_team_name: targetName,
      target_team_signature: targetSignature,
      rename_target_team_name: normalizeImportString(importedTeamName) !== targetName ? normalizeImportString(importedTeamName) : undefined,
      confidence,
      reason: 'near_match',
      same_parent: sameParent,
    })
  })

  const sameParentCandidates = candidates.filter((candidate) => candidate.same_parent)
  const fallbackCandidates = candidates.filter((candidate) => {
    return candidate.confidence >= CROSS_PARENT_TEAM_MAPPING_SCORE_THRESHOLD
  })
  const selectedCandidates = sameParentCandidates.length > 0 ? sameParentCandidates : fallbackCandidates

  return _.orderBy(selectedCandidates, ['confidence', 'target_team_name'], ['desc', 'asc'])
    .map((candidate) => _.omit(candidate, 'same_parent')) as ImportTeamMappingSuggestion[]
}

const buildExistingByEmail = (
  existingMembers: any[],
  teamByKey: Record<string, any>,
  defaults: ImportDefaults
) => {
  const existingByEmail = new Map<string, ExistingImportMember>()
  const signatureCache = new Map<string, string>()
  existingMembers.forEach((member: any) => {
    const email = normalizeImportEmail(member.email)
    if (!email) return
    const teamId = normalizeImportString(member.team)
    const teamName = normalizeImportString(member.team_name || _.get(teamByKey, `${teamId}.name`, ''))
    existingByEmail.set(email, {
      first_name: normalizeImportString(member.firstname),
      last_name: normalizeImportString(member.lastname),
      email,
      team_name: teamName,
      team_id: teamId,
      team_signature: teamId ? buildCurrentTeamSignature(teamId, teamByKey, signatureCache) : normalizeImportHeader(teamName),
      direct_manager_email: normalizeImportEmail(member.boss),
      manager_name: '',
      stored_user_type: normalizeImportString(member.user_type),
      user_type: normalizeImportUserType(member.user_type, 'user'),
      language: normalizeImportLanguage(member.language, defaults.defaultLanguage),
      country: normalizeImportCountry(member.country, defaults.companyCountry, defaults.locationOptions),
      time_zone: normalizeImportTimezone(_.get(member, 'timezone', defaults.defaultTimezone), defaults.defaultTimezone).value,
      nickname: normalizeImportString(member.nickname),
      birthday: normalizeStoredBirthday(member.birthday),
      workdate: normalizeStoredWorkdate(member.workdate),
    })
  })
  return existingByEmail
}

const addIssue = (member: NormalizedImportMember, error: string, recommendedFix: string) => {
  if (!member._issues.some((item) => item.error === error && item.recommended_fix === recommendedFix)) {
    member._issues.push({ error, recommended_fix: recommendedFix })
  }
}

const addWarning = (member: NormalizedImportMember, message: string) => {
  if (message && member._warnings.indexOf(message) === -1) {
    member._warnings.push(message)
  }
}

const setSuggestedTeamName = (
  member: NormalizedImportMember,
  teamName: string,
  manager: any,
  suggestedNameOverride = ''
) => {
  if (member._suggested_team_name) {
    return
  }

  const suggestedName = suggestedNameOverride || buildSuggestedManagedTeamName(teamName, manager)
  if (!suggestedName || normalizeImportString(suggestedName) === normalizeImportString(teamName)) {
    return
  }

  member._suggested_team_name = suggestedName
}

const addGlobalIssue = (members: NormalizedImportMember[], error: string, recommendedFix: string, emails: string[] = []) => {
  let targets = members.filter((member) => emails.indexOf(member.email) !== -1)
  if (targets.length === 0 && members.length > 0) {
    targets = [members[0]]
  }
  targets.forEach((member) => addIssue(member, error, recommendedFix))
}

const buildPreparedMemberFromRow = (
  row: any[],
  index: number,
  headerIndex: ImportHeaderIndex,
  headerPresence: ImportHeaderPresence,
  existingByEmail: Map<string, ExistingImportMember>,
  defaults: ImportDefaults,
  isProfileOnlyImport: boolean
): NormalizedImportMember | null => {
  const email = normalizeImportEmail(_.get(row, headerIndex.email, ''))
  const existing = email ? existingByEmail.get(email) || null : null
  const importedFirstName = normalizeImportString(_.get(row, headerIndex.firstname, ''))
  const importedLastName = normalizeImportString(_.get(row, headerIndex.lastname, ''))
  const importedTeamName = normalizeImportString(_.get(row, headerIndex.team_name, ''))
  const importedManagerEmail = normalizeImportEmail(_.get(row, headerIndex.boss, ''))
  const importedNickname = normalizeImportString(_.get(row, headerIndex.nickname, ''))
  const birthdayInput = _.get(row, headerIndex.birthday, '')
  const joinDateInput = _.get(row, headerIndex.join_date, '')
  const timezoneResult = normalizeImportTimezone(
    _.get(row, headerIndex.timezone, ''),
    _.get(existing, 'time_zone', defaults.defaultTimezone)
  )
  const birthdayResult = normalizeImportBirthday(birthdayInput)
  const joinDateResult = normalizeImportDate(joinDateInput)
  const hasNicknameUpdate = headerPresence.nickname && importedNickname !== ''
  const hasBirthdayUpdate = headerPresence.birthday && normalizeImportString(birthdayInput) !== ''
  const hasWorkdateUpdate = headerPresence.join_date && normalizeImportString(joinDateInput) !== ''
  const languageResult = headerPresence.language
    ? normalizeImportLanguageWithWarning(_.get(row, headerIndex.language, ''), _.get(existing, 'language', defaults.defaultLanguage))
    : { value: _.get(existing, 'language', defaults.defaultLanguage), warning: '' }
  const userTypeResult = headerPresence.user_type
    ? normalizeImportUserTypeWithWarning(_.get(row, headerIndex.user_type, ''), _.get(existing, 'user_type', '') || 'user')
    : { value: _.get(existing, 'user_type', '') || 'user', warning: '' }
  const countryResult = headerPresence.country
    ? normalizeImportCountryWithWarning(_.get(row, headerIndex.country, ''), _.get(existing, 'country', defaults.companyCountry), defaults.locationOptions)
    : { value: _.get(existing, 'country', defaults.companyCountry), warning: '' }

  const member: NormalizedImportMember = {
    row: index + 1,
    first_name: headerPresence.firstname ? importedFirstName : _.get(existing, 'first_name', ''),
    last_name: headerPresence.lastname ? importedLastName : _.get(existing, 'last_name', ''),
    email,
    team_name: headerPresence.team_name ? importedTeamName : _.get(existing, 'team_name', ''),
    direct_manager_email: headerPresence.boss ? importedManagerEmail : _.get(existing, 'direct_manager_email', ''),
    manager_name: '',
    user_type: userTypeResult.value,
    language: languageResult.value,
    country: countryResult.value,
    time_zone: timezoneResult.value,
    nickname: hasNicknameUpdate ? importedNickname : _.get(existing, 'nickname', ''),
    birthday: hasBirthdayUpdate ? birthdayResult.value : _.get(existing, 'birthday', ''),
    workdate: hasWorkdateUpdate ? joinDateResult.value : _.get(existing, 'workdate', ''),
    _issues: [],
    _warnings: [],
    _profile_only: isProfileOnlyImport,
    _existing: existing ? _.cloneDeep(existing) : null,
    _column_presence: cloneHeaderPresence(headerPresence),
    _optional_fields: {
      nickname: hasNicknameUpdate,
      birthday: hasBirthdayUpdate,
      workdate: hasWorkdateUpdate,
    },
    _suggested_team_name: '',
    _imported_team_name: headerPresence.team_name ? importedTeamName : _.get(existing, 'team_name', ''),
    _team_mapping_suggestion: null,
    _team_mapping_decision: null,
  }

  const hasAnyValue = [
    importedFirstName,
    importedLastName,
    email,
    importedTeamName,
    importedManagerEmail,
    importedNickname,
    normalizeImportString(birthdayInput),
    normalizeImportString(joinDateInput),
  ].some(Boolean)

  if (!hasAnyValue) {
    return null
  }

  if (languageResult.warning) addWarning(member, languageResult.warning)
  if (userTypeResult.warning) addWarning(member, userTypeResult.warning)
  if (countryResult.warning) addWarning(member, countryResult.warning)
  if (timezoneResult.error) addIssue(member, 'Timezone is invalid', timezoneResult.error)
  if (member._optional_fields.birthday && birthdayResult.error) {
    addIssue(member, 'Birthday is invalid', birthdayResult.error)
  }
  if (member._optional_fields.workdate && joinDateResult.error) {
    addIssue(member, 'Join date is invalid', joinDateResult.error)
  }
  if (member._optional_fields.birthday && birthdayResult.warning) {
    addWarning(member, birthdayResult.warning)
  }
  if (member._optional_fields.workdate && joinDateResult.warning) {
    addWarning(member, joinDateResult.warning)
  }

  return member
}

const clonePreparedReviewItem = (item: any): NormalizedImportMember => {
  return {
    row: _.get(item, 'row', 0),
    first_name: normalizeImportString(_.get(item, 'first_name', '')),
    last_name: normalizeImportString(_.get(item, 'last_name', '')),
    email: normalizeImportEmail(_.get(item, 'email', '')),
    team_name: normalizeImportString(_.get(item, 'team_name', '')),
    direct_manager_email: normalizeImportEmail(_.get(item, 'direct_manager_email', '')),
    manager_name: normalizeImportString(_.get(item, 'manager_name', '')),
    user_type: normalizeImportUserType(_.get(item, 'user_type', ''), 'user'),
    language: normalizeImportLanguage(_.get(item, 'language', ''), 'en'),
    country: normalizeImportString(_.get(item, 'country', '')),
    time_zone: normalizeImportTimezone(_.get(item, 'time_zone', ''), '+07:00').value,
    nickname: normalizeImportString(_.get(item, 'nickname', '')),
    birthday: normalizeStoredBirthday(_.get(item, 'birthday', '')),
    workdate: normalizeStoredWorkdate(_.get(item, 'workdate', '')),
    _issues: [],
    _warnings: [],
    _profile_only: !!_.get(item, '_profile_only', false),
    _existing: _.get(item, '_existing', null),
    _column_presence: cloneHeaderPresence(_.get(item, '_column_presence', {
      firstname: true,
      lastname: true,
      email: true,
      team_name: true,
      boss: true,
      language: true,
      country: true,
      timezone: true,
      user_type: true,
      nickname: false,
      birthday: false,
      join_date: false,
    })),
    _optional_fields: cloneOptionalFields(_.get(item, '_optional_fields', {
      nickname: false,
      birthday: false,
      workdate: false,
    })),
    _suggested_team_name: normalizeImportString(_.get(item, '_suggested_team_name', '')),
    _imported_team_name: normalizeImportString(_.get(item, '_imported_team_name', _.get(item, 'team_name', ''))),
    _team_mapping_suggestion: null,
    _team_mapping_decision: cloneTeamMappingDecision(_.get(item, '_team_mapping_decision', null)),
  }
}

const buildTeamIndexes = (existingMembers: any[], teamByKey: Record<string, any>) => {
  const signatureCache = new Map<string, string>()
  const teamIdsByName = new Map<string, string[]>()
  const teamIdsBySignature = new Map<string, string[]>()
  const teamMembersById = new Map<string, string[]>()

  Object.keys(teamByKey || {}).forEach((teamId) => {
    const signature = buildCurrentTeamSignature(teamId, teamByKey, signatureCache)
    const normalizedName = normalizeImportHeader(_.get(teamByKey, `${teamId}.name`, ''))
    if (!teamIdsByName.has(normalizedName)) {
      teamIdsByName.set(normalizedName, [])
    }
    const teamNameList = teamIdsByName.get(normalizedName)
    if (teamNameList) {
      teamNameList.push(teamId)
    }
    if (!teamIdsBySignature.has(signature)) {
      teamIdsBySignature.set(signature, [])
    }
    const teamSignatureList = teamIdsBySignature.get(signature)
    if (teamSignatureList) {
      teamSignatureList.push(teamId)
    }
  })

  existingMembers.forEach((member: any) => {
    const email = normalizeImportEmail(member.email)
    const teamId = normalizeImportString(member.team)
    if (!email || !teamId) return
    if (!teamMembersById.has(teamId)) {
      teamMembersById.set(teamId, [])
    }
    const teamMemberList = teamMembersById.get(teamId)
    if (teamMemberList) {
      teamMemberList.push(email)
    }
  })

  return {
    signatureCache,
    teamIdsByName,
    teamIdsBySignature,
    teamMembersById,
  }
}

const evaluatePreparedMembers = (
  preparedMembers: NormalizedImportMember[],
  existingMembers: any[],
  teamByKey: Record<string, any>,
  defaults: ImportDefaults,
  archivedMembersByKey: Record<string, any> = {},
  teamImportAliases: Record<string, any> = {}
): ImportPreviewResult => {
  const existingByEmail = buildExistingByEmail(existingMembers, teamByKey, defaults)
  const importedMembers = preparedMembers.map((item) => {
    const existing = item.email ? existingByEmail.get(item.email) || null : null
    return {
      ..._.cloneDeep(item),
      manager_name: '',
      _issues: _.cloneDeep(_.get(item, '_issues', [])),
      _warnings: _.cloneDeep(_.get(item, '_warnings', [])),
      _existing: existing ? _.cloneDeep(existing) : null,
      _suggested_team_name: '',
      _imported_team_name: normalizeImportString(_.get(item, '_imported_team_name', _.get(item, 'team_name', ''))),
      _team_mapping_suggestion: null,
      _team_mapping_decision: cloneTeamMappingDecision(_.get(item, '_team_mapping_decision', null)),
    }
  })
  const importedEmailCount = new Map<string, number>()

  importedMembers.forEach((member) => {
    if (member.email) {
      importedEmailCount.set(member.email, (importedEmailCount.get(member.email) || 0) + 1)
    }
  })

  importedMembers.forEach((member) => {
    if (!member.email) {
      addIssue(member, 'Email address or phone number is required', 'Please provide a valid work email address or an international phone number with country code.')
    } else if (!isValidImportIdentifier(member.email)) {
      addIssue(member, 'Email address or phone number is invalid', 'Use a valid work email address or an international phone number with country code for each member.')
    }
    if (member.email && (importedEmailCount.get(member.email) || 0) > 1) {
      addIssue(member, 'Email address or phone number is duplicated in the import file', 'Keep only one row per email address or phone number in the import file.')
    }
    if (member._profile_only) {
      if (!existingByEmail.has(member.email)) {
        addIssue(member, 'Member does not exist', 'Profile-only imports can update existing members only.')
      }
      if (!member._optional_fields.nickname && !member._optional_fields.birthday && !member._optional_fields.workdate) {
        addIssue(member, 'No profile fields to update', 'Include at least one of Nickname, Birthday, or Join Date.')
      }
      return
    }
    if (!member.first_name) {
      addIssue(member, 'First name is required', 'Please provide a valid first name.')
    }
    if (!member.last_name) {
      addIssue(member, 'Last name is required', 'Please provide a valid last name.')
    }
    if (!member.team_name) {
      addIssue(member, 'Team name is required', 'Please provide a team name for each member.')
    }
  })

  const finalMembersByEmail = new Map<string, ExistingImportMember | NormalizedImportMember>(existingByEmail as any)
  importedMembers.forEach((member) => {
    if (!member.email) return
    const existing = existingByEmail.get(member.email) || {} as ExistingImportMember
    finalMembersByEmail.set(member.email, {
      ...existing,
      ...member,
    } as any)
  })

  importedMembers.forEach((member) => {
    if (member._profile_only) return
    if (!member.email || !member.direct_manager_email) return
    if (member.direct_manager_email === member.email) {
      addIssue(member, 'Manager assignment creates a reporting loop', 'A member cannot be their own direct manager.')
      return
    }
    if (!finalMembersByEmail.has(member.direct_manager_email)) {
      addIssue(member, 'Manager does not exist', `Ensure manager ${member.direct_manager_email} already exists or is included in the same import.`)
      return
    }
  })

  const reportCounts = new Map<string, number>()
  finalMembersByEmail.forEach((member: any) => {
    if (!member.direct_manager_email) return
    reportCounts.set(member.direct_manager_email, (reportCounts.get(member.direct_manager_email) || 0) + 1)
  })

  const existingRootEmails = new Set<string>()
  existingByEmail.forEach((member) => {
    if (!member.direct_manager_email) {
      existingRootEmails.add(member.email)
    }
  })
  const importedRootEmails = importedMembers
    .filter((member) => !member._profile_only && !member.direct_manager_email)
    .map((member) => member.email)
  const newlyCreatedRootEmails = importedRootEmails.filter((email) => !existingRootEmails.has(email))
  // A root handover (new root + every previous root now reports to someone in
  // this same import) is valid, so only count existing roots that stay roots.
  const remainingExistingRootEmails = Array.from(existingRootEmails).filter((email) => {
    const finalMember = finalMembersByEmail.get(email) as any
    return !normalizeImportEmail(_.get(finalMember, 'direct_manager_email', ''))
  })
  const importCreatesAnotherRoot = newlyCreatedRootEmails.length > 0 && remainingExistingRootEmails.length > 0
  if (importedRootEmails.length > 1 || importCreatesAnotherRoot) {
    const targetEmails = importedRootEmails.length > 1 ? importedRootEmails : newlyCreatedRootEmails
    addGlobalIssue(importedMembers, 'Exactly one person in the org may have no direct manager', 'Leave the direct manager blank for only the single top-most person in the org.', targetEmails)
  }

  const visitState = new Map<string, number>()
  const visitStack: string[] = []
  const cycleEmails = new Set<string>()
  const visitManagerChain = (email: string) => {
    const currentState = visitState.get(email)
    if (currentState === 1) {
      const startIndex = visitStack.indexOf(email)
      visitStack.slice(startIndex).forEach((entry) => cycleEmails.add(entry))
      return
    }
    if (currentState === 2) return

    visitState.set(email, 1)
    visitStack.push(email)
    const member = finalMembersByEmail.get(email) as any
    const managerEmail = _.get(member, 'direct_manager_email', '')
    if (managerEmail && finalMembersByEmail.has(managerEmail)) {
      visitManagerChain(managerEmail)
    }
    visitStack.pop()
    visitState.set(email, 2)
  }

  finalMembersByEmail.forEach((_: any, email: string) => {
    visitManagerChain(email)
  })

  if (cycleEmails.size > 0 && importedMembers.some((member) => !member._profile_only)) {
    const targetEmails = importedMembers
      .filter((member) => cycleEmails.has(member.email) || cycleEmails.has(member.direct_manager_email))
      .map((member) => member.email)
    // A cycle that only involves members outside this file is a pre-existing
    // data problem the import cannot cause or fix — don't block on it.
    if (targetEmails.length > 0) {
      addGlobalIssue(importedMembers, 'Manager assignment creates a reporting loop', 'Remove circular reporting relationships before importing.', targetEmails)
    }
  }

  reportCounts.forEach((_: number, managerEmail: string) => {
    const managerMember = finalMembersByEmail.get(managerEmail) as any
    if (!managerMember) return

    if (!managerMember.team_name) {
      if (importedMembers.some((member) => member.email === managerEmail)) {
        addGlobalIssue(importedMembers, 'Managers must have a team name', 'Give each manager a team name before importing.', [managerEmail])
      }
      return
    }

    if (managerMember.team_name.length > 15 && importedMembers.some((member) => member.email === managerEmail)) {
      const importedManager = importedMembers.find((member) => member.email === managerEmail)
      if (importedManager) {
        addWarning(importedManager, `Team name '${managerMember.team_name}' is longer than 15 characters. Shorter team names display better in the app.`)
      }
    }
  })

  const importedEmails = new Set(importedMembers.filter((member) => !member._profile_only).map((member) => member.email))
  const teamIndexes = buildTeamIndexes(existingMembers, teamByKey)
  const intendedSignatureCache = new Map<string, string>()
  const getIntendedTeamSignature = (email: string, visiting = new Set<string>()) => {
    if (!email) return ''
    if (intendedSignatureCache.has(email)) {
      return intendedSignatureCache.get(email) as string
    }
    if (visiting.has(email)) {
      return ''
    }
    visiting.add(email)
    const member = finalMembersByEmail.get(email) as any
    const teamName = normalizeImportHeader(_.get(member, 'team_name', ''))
    if (!teamName) {
      intendedSignatureCache.set(email, '')
      visiting.delete(email)
      return ''
    }
    const managerEmail = normalizeImportEmail(_.get(member, 'direct_manager_email', ''))
    let signature = teamName
    if (managerEmail && finalMembersByEmail.has(managerEmail)) {
      const managerMember = finalMembersByEmail.get(managerEmail) as any
      const managerTeamName = normalizeImportHeader(_.get(managerMember, 'team_name', ''))
      const managerSignature = getIntendedTeamSignature(managerEmail, visiting)
      signature = managerTeamName && managerTeamName === teamName
        ? managerSignature || teamName
        : managerSignature ? `${managerSignature}>${teamName}` : teamName
    }
    intendedSignatureCache.set(email, signature)
    visiting.delete(email)
    return signature
  }

  const importedMembersByTeamName = new Map<string, NormalizedImportMember[]>()
  importedMembers.filter((member) => !member._profile_only && member.team_name).forEach((member) => {
    const teamNameKey = normalizeImportHeader(member.team_name)
    if (!importedMembersByTeamName.has(teamNameKey)) {
      importedMembersByTeamName.set(teamNameKey, [])
    }
    member._target_team_signature = getIntendedTeamSignature(member.email)
    const teamNameMembers = importedMembersByTeamName.get(teamNameKey)
    if (teamNameMembers) {
      teamNameMembers.push(member)
    }
  })

  importedMembersByTeamName.forEach((teamMembers, teamNameKey) => {
    const intendedSignatures = _.uniq(teamMembers.map((member) => member._target_team_signature || '').filter(Boolean))
    const currentTeamIds = teamIndexes.teamIdsByName.get(teamNameKey) || []
    if (intendedSignatures.length > 1) {
      if (currentTeamIds.length === 1) {
        const currentTeamId = currentTeamIds[0]
        const currentSignature = buildCurrentTeamSignature(currentTeamId, teamByKey, teamIndexes.signatureCache)
        teamMembers.forEach((member) => {
          member._resolved_team_id = currentTeamId
          member._source_team_signature = currentSignature
          member._target_team_signature = currentSignature
          member._team_action = 'reuse'
        })
        return
      }

      if (currentTeamIds.length > 1) {
        teamMembers.forEach((member) => {
          const manager = finalMembersByEmail.get(member.direct_manager_email) as any
          setSuggestedTeamName(member, member.team_name, manager)
          addIssue(member, `Team '${member.team_name}' matches multiple existing teams`, buildAmbiguousTeamMatchFix(member.team_name, manager))
        })
        return
      }

      const nearMatchesBySignature = intendedSignatures.map((signature) => {
        return {
          signature,
          matches: findNearTeamMatches(
            teamMembers[0]._imported_team_name || teamMembers[0].team_name,
            signature,
            teamByKey,
            teamIndexes.signatureCache
          ),
        }
      })
      const allSignaturesHaveOneMatch = nearMatchesBySignature.every((entry) => entry.matches.length === 1)
      const uniqueMatchedTargetIds = _.uniq(nearMatchesBySignature.map((entry) => {
        return entry.matches[0] ? entry.matches[0].target_team_id : ''
      }).filter(Boolean))
      if (allSignaturesHaveOneMatch && uniqueMatchedTargetIds.length === 1) {
        teamMembers.forEach((member) => {
          const signature = member._target_team_signature || ''
          const entry = nearMatchesBySignature.find((item) => item.signature === signature)
          const suggestion = entry && entry.matches[0]
          if (!suggestion) {
            return
          }
          member._team_mapping_suggestion = { ...suggestion }
          addIssue(
            member,
            `Team '${member.team_name}' may already exist as '${suggestion.target_team_name}'`,
            `Map this imported team to existing team '${suggestion.target_team_name}' to preserve team history, or rename the team in the import if this should be a separate new team.`
          )
        })
        return
      }

      const ambiguousMatches = nearMatchesBySignature
        .filter((entry) => entry.matches.length > 1)
        .flatMap((entry) => entry.matches)
      if (ambiguousMatches.length > 0 || uniqueMatchedTargetIds.length > 1) {
        const possibleMatches = _.uniq(ambiguousMatches.length > 0
          ? ambiguousMatches.map((match) => `'${match.target_team_name}'`)
          : nearMatchesBySignature.flatMap((entry) => entry.matches.map((match) => `'${match.target_team_name}'`)))
          .slice(0, 3)
          .join(', ')
        teamMembers.forEach((member) => {
          addIssue(
            member,
            `Team '${member.team_name}' matches multiple similar existing teams`,
            `Choose the correct existing team by making the team name an exact match, or rename it if this should be a new team. Possible matches: ${possibleMatches}.`
          )
        })
        return
      }

      const suggestedNamesBySignature = buildReportingLineSuggestedTeamNames(teamMembers, finalMembersByEmail)
      teamMembers.forEach((member) => {
        const manager = finalMembersByEmail.get(member.direct_manager_email) as any
        const suggestedName = suggestedNamesBySignature.get(member._target_team_signature || '') || ''
        setSuggestedTeamName(member, member.team_name, manager, suggestedName)
        addIssue(
          member,
          `Team '${member.team_name}' is used with more than one reporting line`,
          buildTeamReportingLineFix(member.team_name, manager, suggestedName)
        )
      })
      return
    }

    const intendedSignature = intendedSignatures[0] || ''
    const exactSignatureTeamIds = currentTeamIds.filter((teamId) => {
      return buildCurrentTeamSignature(teamId, teamByKey, teamIndexes.signatureCache) === intendedSignature
    })

    if (exactSignatureTeamIds.length === 1) {
      const currentTeamId = exactSignatureTeamIds[0]
      const currentSignature = buildCurrentTeamSignature(currentTeamId, teamByKey, teamIndexes.signatureCache)
      teamMembers.forEach((member) => {
        member._resolved_team_id = currentTeamId
        member._source_team_signature = currentSignature
        member._target_team_signature = currentSignature
        member._team_action = 'reuse'
      })
      return
    }

    if (exactSignatureTeamIds.length > 1) {
      teamMembers.forEach((member) => {
        const manager = finalMembersByEmail.get(member.direct_manager_email) as any
        setSuggestedTeamName(member, member.team_name, manager)
        addIssue(member, `Team '${member.team_name}' matches multiple existing teams`, buildAmbiguousTeamMatchFix(member.team_name, manager))
      })
      return
    }

    if (currentTeamIds.length > 1) {
      teamMembers.forEach((member) => {
        const manager = finalMembersByEmail.get(member.direct_manager_email) as any
        setSuggestedTeamName(member, member.team_name, manager)
        addIssue(member, `Team '${member.team_name}' matches multiple existing teams`, buildAmbiguousTeamMatchFix(member.team_name, manager))
      })
      return
    }

    if (currentTeamIds.length === 0) {
      const selectedDecisions = teamMembers
        .map((member) => cloneTeamMappingDecision(member._team_mapping_decision))
        .filter(Boolean) as ImportTeamMappingDecision[]
      const selectedTargetIds = _.uniq(selectedDecisions.map((decision) => decision.target_team_id))
      if (selectedTargetIds.length > 1) {
        teamMembers.forEach((member) => {
          addIssue(member, `Team '${member.team_name}' has conflicting mapping decisions`, 'Map all rows for the same imported team and org path to one existing team.')
        })
        return
      }

      if (selectedDecisions.length > 0) {
        const selectedDecision = buildMappingDecisionFromTarget({
          importedTeamName: teamMembers[0]._imported_team_name || teamMembers[0].team_name,
          importedTeamSignature: intendedSignature,
          targetTeamId: selectedDecisions[0].target_team_id,
          teamByKey,
          signatureCache: teamIndexes.signatureCache,
          source: selectedDecisions[0].source,
          aliasKey: selectedDecisions[0].alias_key,
          renameTargetTeamName: selectedDecisions[0].rename_target_team_name,
        })

        if (!selectedDecision) {
          teamMembers.forEach((member) => {
            addIssue(member, `Mapped team for '${member.team_name}' no longer exists`, 'Choose another existing team or remove the mapping before importing.')
          })
          return
        }

        teamMembers.forEach((member) => {
          member._team_action = 'map'
          member._resolved_team_id = selectedDecision.target_team_id
          member._source_team_signature = selectedDecision.target_team_signature
          member._target_team_signature = selectedDecision.target_team_signature
          member._team_mapping_decision = { ...selectedDecision }
          if (selectedDecision.rename_target_team_name) {
            addWarning(member, `Will map '${selectedDecision.imported_team_name}' to existing team '${selectedDecision.target_team_name}' and rename it to '${selectedDecision.rename_target_team_name}' to preserve team history.`)
          } else {
            addWarning(member, `Will map '${selectedDecision.imported_team_name}' to existing team '${selectedDecision.target_team_name}' to preserve team history.`)
          }
        })
        return
      }

      const savedAliasDecision = getSavedTeamAliasDecision(
        teamImportAliases,
        teamMembers[0]._imported_team_name || teamMembers[0].team_name,
        intendedSignature,
        teamByKey,
        teamIndexes.signatureCache
      )

      if (savedAliasDecision) {
        teamMembers.forEach((member) => {
          member._team_action = 'map'
          member._resolved_team_id = savedAliasDecision.target_team_id
          member._source_team_signature = savedAliasDecision.target_team_signature
          member._target_team_signature = savedAliasDecision.target_team_signature
          member._team_mapping_decision = { ...savedAliasDecision }
          addWarning(member, `Mapped from saved team alias '${savedAliasDecision.imported_team_name}' -> '${savedAliasDecision.target_team_name}'.`)
        })
        return
      }

      const nearMatches = findNearTeamMatches(
        teamMembers[0]._imported_team_name || teamMembers[0].team_name,
        intendedSignature,
        teamByKey,
        teamIndexes.signatureCache
      )

      if (nearMatches.length === 1) {
        const suggestion = nearMatches[0]
        teamMembers.forEach((member) => {
          member._team_mapping_suggestion = { ...suggestion }
          addIssue(
            member,
            `Team '${member.team_name}' may already exist as '${suggestion.target_team_name}'`,
            `Map this imported team to existing team '${suggestion.target_team_name}' to preserve team history, or rename the team in the import if this should be a separate new team.`
          )
        })
        return
      }

      if (nearMatches.length > 1) {
        const possibleMatches = nearMatches.slice(0, 3).map((match) => `'${match.target_team_name}'`).join(', ')
        teamMembers.forEach((member) => {
          addIssue(
            member,
            `Team '${member.team_name}' matches multiple similar existing teams`,
            `Choose the correct existing team by making the team name an exact match, or rename it if this should be a new team. Possible matches: ${possibleMatches}.`
          )
        })
        return
      }

      teamMembers.forEach((member) => {
        member._team_action = 'create'
        member._target_team_signature = intendedSignature
        addWarning(member, `New team '${member.team_name}' will be created with no prior team history.`)
      })
      return
    }

    const currentTeamId = currentTeamIds[0]
    const currentSignature = buildCurrentTeamSignature(currentTeamId, teamByKey, teamIndexes.signatureCache)
    teamMembers.forEach((member) => {
      member._resolved_team_id = currentTeamId
      member._source_team_signature = currentSignature
      member._target_team_signature = intendedSignature
    })

    if (currentSignature === intendedSignature) {
      teamMembers.forEach((member) => {
        member._team_action = 'reuse'
      })
      return
    }

    const currentTeamMembers = teamIndexes.teamMembersById.get(currentTeamId) || []
    const missingMembers = currentTeamMembers.filter((email) => !importedEmails.has(email))
    if (missingMembers.length > 0) {
      teamMembers.forEach((member) => {
        const manager = finalMembersByEmail.get(member.direct_manager_email) as any
        setSuggestedTeamName(member, member.team_name, manager)
        addIssue(member, `Team '${member.team_name}' would move existing members not included in this import`, buildPartialTeamMoveFix(member.team_name, manager))
      })
      return
    }

    teamMembers.forEach((member) => {
      member._team_action = 'move'
    })
  })

  importedMembers.forEach((member) => {
    const manager = finalMembersByEmail.get(member.direct_manager_email) as any
    member.manager_name = manager ? buildImportName(manager.first_name, manager.last_name) : ''
  })

  const dataGroups = ROOT_GROUPS()
  let total = 0
  importedMembers.forEach((member) => {
    const emailKey = member.email ? member.email.replace(/\./g, '||') : ''
    if (emailKey && _.has(archivedMembersByKey, emailKey)) {
      // Surface archived matches instead of silently dropping the row so the
      // admin learns why this person will not be imported.
      addIssue(member, 'Member is archived', 'Restore this member from the archived members list first, or remove this row from the import file.')
    }

    const existing = existingByEmail.get(member.email)
    if (member._issues.length > 0) {
      member.error = member._issues.map((item) => item.error).join(' | ')
      member.note = _.uniq(member._issues.map((item) => item.recommended_fix).concat(member._warnings)).join(' ')
      dataGroups[3].items.push(member)
      total++
      return
    }

    const resolvedTeamId = normalizeImportString(member._resolved_team_id || '')
    const teamChanged = !member._profile_only && (
      resolvedTeamId
        ? existing && existing.team_id !== resolvedTeamId
        : existing && existing.team_name !== member.team_name
    )
    const isUpdated = !!existing && (
      (
        !member._profile_only && (
          existing.first_name !== member.first_name ||
          existing.last_name !== member.last_name ||
          teamChanged ||
          existing.direct_manager_email !== member.direct_manager_email ||
          existing.language !== member.language ||
          existing.country !== member.country ||
          existing.time_zone !== member.time_zone ||
          existing.user_type !== member.user_type ||
          normalizeImportHeader(existing.stored_user_type) !== normalizeImportHeader(member.user_type)
        )
      ) ||
      (member._optional_fields.nickname && existing.nickname !== member.nickname) ||
      (member._optional_fields.birthday && existing.birthday !== member.birthday) ||
      (member._optional_fields.workdate && existing.workdate !== member.workdate)
    )

    member.note = member._warnings.length > 0 ? _.uniq(member._warnings).join(' ') : 'Ready to import'

    if (existing) {
      if (!isUpdated && member._warnings.length === 0) {
        return
      }
      dataGroups[1].items.push(member)
    } else {
      dataGroups[0].items.push(member)
    }
    total++
  })

  return {
    dataGroups,
    total,
    importedMembers,
    existingByEmail,
  }
}

export const buildImportPreview = (args: {
  rows: any[]
  existingMembers: any[]
  teamByKey: Record<string, any>
  defaults: ImportDefaults
  archivedMembersByKey?: Record<string, any>
  teamImportAliases?: Record<string, any>
}) => {
  const rows = _.cloneDeep(Array.isArray(args.rows) ? args.rows : [])
  const header = rows.shift()

  if (!Array.isArray(header)) {
    return {
      dataGroups: [],
      total: 0,
      importedMembers: [],
      existingByEmail: new Map<string, ExistingImportMember>(),
    }
  }

  const headerIndex = buildImportHeaderIndex(header)
  if (headerIndex.email === -1) {
    return createMissingIdentifierResult()
  }

  const headerPresence = buildImportHeaderPresence(headerIndex)
  const isProfileOnlyImport =
    headerIndex.email !== -1 &&
    headerIndex.firstname === -1 &&
    headerIndex.lastname === -1 &&
    headerIndex.team_name === -1 &&
    headerIndex.boss === -1 &&
    headerIndex.language === -1 &&
    headerIndex.country === -1 &&
    headerIndex.timezone === -1 &&
    headerIndex.user_type === -1 &&
    (headerPresence.nickname || headerPresence.birthday || headerPresence.join_date)

  const existingByEmail = buildExistingByEmail(args.existingMembers, args.teamByKey, args.defaults)
  const preparedMembers = rows
    .map((row: any, index: number) => {
      const values = Array.isArray(row) ? row : []
      return buildPreparedMemberFromRow(values, index, headerIndex, headerPresence, existingByEmail, args.defaults, isProfileOnlyImport)
    })
    .filter(Boolean) as NormalizedImportMember[]

  return evaluatePreparedMembers(preparedMembers, args.existingMembers, args.teamByKey, args.defaults, args.archivedMembersByKey, args.teamImportAliases)
}

export const buildImportEvaluationFromReviewItems = (args: {
  items: any[]
  existingMembers: any[]
  teamByKey: Record<string, any>
  defaults: ImportDefaults
  archivedMembersByKey?: Record<string, any>
  teamImportAliases?: Record<string, any>
}) => {
  const preparedMembers = _.cloneDeep(Array.isArray(args.items) ? args.items : []).map((item: any) => clonePreparedReviewItem(item))
  return evaluatePreparedMembers(preparedMembers, args.existingMembers, args.teamByKey, args.defaults, args.archivedMembersByKey, args.teamImportAliases)
}

const buildOrderedItems = (items: NormalizedImportMember[]) => {
  const actionableByEmail = new Map<string, NormalizedImportMember>()
  items.forEach((item) => {
    actionableByEmail.set(normalizeImportEmail(item.email), item)
  })

  const depthCache = new Map<string, number>()
  const getDepth = (email: string, visiting = new Set<string>()) => {
    if (depthCache.has(email)) {
      return depthCache.get(email) as number
    }
    if (visiting.has(email)) {
      return 0
    }
    visiting.add(email)
    const item = actionableByEmail.get(email)
    if (!item || !item.direct_manager_email || !actionableByEmail.has(item.direct_manager_email)) {
      depthCache.set(email, 0)
      visiting.delete(email)
      return 0
    }
    const depth = getDepth(item.direct_manager_email, visiting) + 1
    depthCache.set(email, depth)
    visiting.delete(email)
    return depth
  }

  return _.orderBy(
    items,
    [
      (item: NormalizedImportMember) => getDepth(normalizeImportEmail(item.email)),
      (item: NormalizedImportMember) => _.get(item, 'row', 0),
    ],
    ['asc', 'asc']
  )
}

export const buildImportMutationPlan = (args: {
  actionableItems: NormalizedImportMember[]
  allUser: Record<string, any>
  teamByKey: Record<string, any>
  companyId: string
  nowDate: string
  nowTimestamp?: string
  actorEmail?: string
  idFactory: ImportPlanIdFactory
}) => {
  const orderedItems = buildOrderedItems(args.actionableItems)
  const updates: Record<string, any> = {}
  const createdUsers: string[] = []
  const updatedUsers: string[] = []
  const createdTeams: string[] = []
  const touchedTeamIds = new Set<string>()
  const savedTeamAliases = new Set<string>()
  const savedTeamAliasScopes = new Set<string>()
  const logs: ImportMutationLog[] = []
  const existingTeamIdsBySignature = new Map<string, string>()
  const signatureCache = new Map<string, string>()
  Object.keys(args.teamByKey || {}).forEach((teamId) => {
    const signature = buildCurrentTeamSignature(teamId, args.teamByKey, signatureCache)
    if (signature && !existingTeamIdsBySignature.has(signature)) {
      existingTeamIdsBySignature.set(signature, teamId)
    }
  })

  const plannedTeamIdsBySignature = new Map<string, string>()
  const signatures = _.uniq(
    orderedItems
      .filter((item) => !item._profile_only && item._target_team_signature)
      .map((item) => item._target_team_signature as string)
  )
  const orderedSignatures = _.sortBy(signatures, (signature) => signature.split('>').length)

  orderedSignatures.forEach((signature) => {
    const teamItems = orderedItems.filter((item) => item._target_team_signature === signature)
    const sample = teamItems[0]
    if (!sample) return
    if (sample._team_action === 'reuse' || sample._team_action === 'move' || sample._team_action === 'map') {
      const existingTeamId = sample._resolved_team_id || existingTeamIdsBySignature.get(signature) || ''
      if (existingTeamId) {
        plannedTeamIdsBySignature.set(signature, existingTeamId)
      }
      return
    }
    const newTeamId = args.idFactory.nextTeamId()
    plannedTeamIdsBySignature.set(signature, newTeamId)
    createdTeams.push(newTeamId)
    const parentSignature = signature.includes('>') ? signature.split('>').slice(0, -1).join('>') : ''
    const parentId = parentSignature ? plannedTeamIdsBySignature.get(parentSignature) || existingTeamIdsBySignature.get(parentSignature) || '' : ''
    const newTeam = {
      name: sample.team_name,
      parent: parentId,
      company: args.companyId,
      internal_id: `t${_.random(100000, 999999)}`,
      updated: args.nowDate,
    }
    updates[`/team/${newTeamId}`] = newTeam
    touchedTeamIds.add(newTeamId)
    logs.push({
      id: newTeamId,
      table: 'team',
      status: 'add',
    })
  })

  const movedTeamIds = new Set<string>()
  orderedItems
    .filter((item) => !item._profile_only && item._team_action === 'move' && item._resolved_team_id && item._target_team_signature)
    .forEach((item) => {
      const teamId = item._resolved_team_id as string
      if (movedTeamIds.has(teamId)) return
      movedTeamIds.add(teamId)
      const targetSignature = item._target_team_signature as string
      const parentSignature = targetSignature.includes('>') ? targetSignature.split('>').slice(0, -1).join('>') : ''
      const parentId = parentSignature ? plannedTeamIdsBySignature.get(parentSignature) || existingTeamIdsBySignature.get(parentSignature) || '' : ''
      updates[`/team/${teamId}/parent`] = parentId
      updates[`/team/${teamId}/updated`] = args.nowDate
      touchedTeamIds.add(teamId)
      logs.push({
        id: teamId,
        table: 'team',
        status: 'edit',
      })
    })

  const renamedTeamIds = new Set<string>()
  orderedItems
    .filter((item) => !item._profile_only && item._team_action === 'map' && item._resolved_team_id && _.get(item, '_team_mapping_decision.source') === 'manual')
    .forEach((item) => {
      const teamId = normalizeImportString(item._resolved_team_id)
      const renameTargetTeamName = normalizeImportString(_.get(item, '_team_mapping_decision.rename_target_team_name', ''))
      const currentTeamName = normalizeImportString(_.get(args.teamByKey, `${teamId}.name`, ''))
      if (!teamId || !renameTargetTeamName || renameTargetTeamName === currentTeamName || renamedTeamIds.has(teamId)) {
        return
      }

      updates[`/team/${teamId}/name`] = renameTargetTeamName
      updates[`/team/${teamId}/updated`] = args.nowDate
      renamedTeamIds.add(teamId)
      touchedTeamIds.add(teamId)
      logs.push({
        id: teamId,
        table: 'team',
        status: 'edit',
      })
    })

  const allUserKeyByNormalized = new Map<string, string>()
  Object.keys(args.allUser || {}).forEach((key) => {
    allUserKeyByNormalized.set(key.toLowerCase(), key)
  })

  orderedItems.forEach((item) => {
    const email = normalizeImportEmail(item.email)
    const normalizedUserKey = email.replace(/\./g, '||')
    if (!normalizedUserKey) {
      // Never emit an empty user key: `updates['/users/']` in a root-level
      // multi-path update would replace the entire users tree.
      return
    }
    // Legacy records can be stored under mixed-case keys; the preview matches
    // them case-insensitively, so the plan must update the stored key rather
    // than create a duplicate lowercase node.
    const existingUserKey = allUserKeyByNormalized.get(normalizedUserKey.toLowerCase()) || ''
    const userKey = existingUserKey || normalizedUserKey
    const existingUser = existingUserKey ? args.allUser[existingUserKey] : null

    if (item._profile_only) {
      const profileUpdatePayload: any = {
        modifydatetime: args.nowDate,
      }
      if (_.get(item, '_optional_fields.nickname', false)) {
        profileUpdatePayload.nickname = normalizeImportString(item.nickname)
      }
      if (_.get(item, '_optional_fields.birthday', false)) {
        profileUpdatePayload.birthday = normalizeStoredBirthday(item.birthday)
      }
      if (_.get(item, '_optional_fields.workdate', false)) {
        profileUpdatePayload.workdate = normalizeStoredWorkdate(item.workdate)
      }
      Object.keys(profileUpdatePayload).forEach((key) => {
        updates[`/users/${userKey}/${key}`] = profileUpdatePayload[key]
      })
      updatedUsers.push(userKey)
      logs.push({
        id: userKey,
        table: 'users',
        status: 'edit',
      })
      return
    }

    const teamSignature = item._target_team_signature || ''
    const teamId = plannedTeamIdsBySignature.get(teamSignature) || existingTeamIdsBySignature.get(teamSignature) || ''
    const timezoneValue = offsetStringToNumber(item.time_zone)

    if (!existingUser) {
      const newUserData: any = {
        firstname: normalizeImportString(item.first_name),
        lastname: normalizeImportString(item.last_name),
        email,
        company: args.companyId,
        team: teamId,
        boss: normalizeImportEmail(item.direct_manager_email),
        language: item.language,
        user_type: item.user_type || 'user',
        gem: 0,
        coin: 0,
        powerup: {
          fivecoins: 14,
          threecoins: 7,
        },
        profilepic: '',
        address: '',
        mobile: '',
        phone: '',
        timezone: timezoneValue,
        country: item.country,
        updated: args.nowDate,
        createdatetime: args.nowDate,
        modifydatetime: args.nowDate,
        nickname: normalizeImportString(item.nickname),
        birthday: normalizeStoredBirthday(item.birthday),
        workdate: normalizeStoredWorkdate(item.workdate),
      }
      updates[`/users/${userKey}`] = newUserData
      createdUsers.push(userKey)
      logs.push({
        id: userKey,
        table: 'users',
        status: 'add',
        source: 'integrations_import',
      })
      return
    }

    const updatePayload: any = {
      firstname: normalizeImportString(item.first_name),
      lastname: normalizeImportString(item.last_name),
      team: teamId,
      boss: normalizeImportEmail(item.direct_manager_email),
      language: item.language,
      user_type: item.user_type || existingUser.user_type || 'user',
      timezone: timezoneValue,
      country: item.country,
      modifydatetime: args.nowDate,
    }
    if (_.get(item, '_optional_fields.nickname', false)) {
      updatePayload.nickname = normalizeImportString(item.nickname)
    }
    if (_.get(item, '_optional_fields.birthday', false)) {
      updatePayload.birthday = normalizeStoredBirthday(item.birthday)
    }
    if (_.get(item, '_optional_fields.workdate', false)) {
      updatePayload.workdate = normalizeStoredWorkdate(item.workdate)
    }
    Object.keys(updatePayload).forEach((key) => {
      updates[`/users/${userKey}/${key}`] = updatePayload[key]
    })
    updatedUsers.push(userKey)
    logs.push({
      id: userKey,
      table: 'users',
      status: 'edit',
    })
  })

  orderedItems
    .filter((item) => !item._profile_only && item._team_action === 'map' && item._team_mapping_decision && item._team_mapping_decision.source === 'manual' && item._resolved_team_id)
    .forEach((item) => {
      const decision = item._team_mapping_decision as ImportTeamMappingDecision
      const importedTeamName = normalizeImportString(decision.imported_team_name || item._imported_team_name || item.team_name)
      const importedTeamSignature = normalizeImportHeader(decision.imported_team_signature || '')
      const targetTeamId = normalizeImportString(decision.target_team_id || item._resolved_team_id)
      const aliasScope = `${importedTeamSignature}|${targetTeamId}`
      if (!importedTeamName || !importedTeamSignature || !targetTeamId || savedTeamAliasScopes.has(aliasScope)) {
        return
      }

      const aliasKey = buildTeamImportAliasKey(importedTeamName, importedTeamSignature)
      const renameTargetTeamName = normalizeImportString(decision.rename_target_team_name)
      const targetTeamName = renameTargetTeamName || normalizeImportString(_.get(args.teamByKey, `${targetTeamId}.name`, decision.target_team_name))
      const currentTargetTeamSignature = buildCurrentTeamSignature(targetTeamId, args.teamByKey, signatureCache)
      const targetTeamSignature = renameTargetTeamName
        ? buildRenamedTeamSignature(currentTargetTeamSignature, renameTargetTeamName)
        : currentTargetTeamSignature
      updates[`/company/${args.companyId}/team_import_aliases/${aliasKey}`] = {
        imported_team_name: importedTeamName,
        imported_team_signature: importedTeamSignature,
        target_team_id: targetTeamId,
        target_team_name: targetTeamName,
        target_team_signature: targetTeamSignature,
        created_at: args.nowTimestamp || args.nowDate,
        updated_at: args.nowTimestamp || args.nowDate,
        created_by: args.actorEmail || '',
      }
      savedTeamAliasScopes.add(aliasScope)
      savedTeamAliases.add(aliasKey)
    })

  if (orderedItems.some((item) => !item._profile_only)) {
    const genBossId = args.idFactory.nextGenBossId()
    updates[`/genboss/${genBossId}`] = {
      company: args.companyId,
    }
  }
  if (createdTeams.length > 0 || movedTeamIds.size > 0 || renamedTeamIds.size > 0) {
    const genOrganizeId = args.idFactory.nextGenOrganizeId()
    updates[`/genorganize/${genOrganizeId}`] = {
      company: args.companyId,
    }
  }

  return {
    updates,
    createdUsers,
    updatedUsers,
    createdTeams,
    touchedTeamIds: Array.from(touchedTeamIds),
    savedTeamAliases: Array.from(savedTeamAliases),
    logs,
  } as ImportMutationPlan
}
